import { ArrowDownward, ArrowUpward, DeleteForever, VisibilityOff } from '@mui/icons-material';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { TranslateText } from '../../../../../../../../application/language/TranslateText';
import { useI18n } from '../../../../../../../../application/language/useI18n';
import { FileType } from '../../../../../../../../service/fileStore/IFileStoreService';
import { IMainLayoutDomainStore } from '../../../../../../../../view/layout/main/store/domain/IMainLayoutDomainStore';
import { ITheoryBlock } from '../../../../../../service/unit/interface/IUnitTheory';
import { IFile } from '../../../../../../service/unit/interface/theoryBlocks/IFile';
import { ImageUrlError, validateImageUrl } from '../../../../../../utils/isSafeUrl';
import { FileUpload } from '../../../../fileUpload/FileUpload';
import { FormTextFieldStandard } from '../../../../form/FormTextFieldStandard';
import { ImageUploadDomain } from './upload/ImageUploadDomain';

export interface ImageProperties {
  data: ITheoryBlock<IFile>;
  onDelete: Function;
  onUp: Function;
  onDown: Function;
  layoutDomain: IMainLayoutDomainStore;
}
export const ImageComponent = observer(
  ({ data: { data, pseudoId }, onDelete, onUp, onDown, layoutDomain }: ImageProperties) => {
    const { translate } = useI18n();
    const [source, setSource] = useState<string>('link');
    const [uploadDomain] = useState<ImageUploadDomain>(new ImageUploadDomain(layoutDomain));
    const [linkValue, setLinkValue] = useState<string>(data.data ?? '');
    const [error, setError] = useState<string | null>(null);

    data.type = 'image';
    return (
      <Box
        key={pseudoId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          background: 'rgba(0,0,0,0.2)',
          padding: '16px 42px ',
          '&:hover .action': { visibility: 'visible' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px', alignItems: 'flex-end' }}>
          <Typography sx={{ marginRight: 'auto' }}>
            <TranslateText i18nKey="createUnit.img" />
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', visibility: 'hidden' }} className="action">
            <DeleteForever
              fontSize="small"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                onDelete();
              }}
            />
            <VisibilityOff fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => {}} />
            <ArrowUpward
              fontSize="small"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                onUp();
              }}
            />
            <ArrowDownward
              fontSize="small"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                onDown();
              }}
            />
          </Box>
        </Box>
        <FormControl sx={{ marginBottom: '32px' }}>
          <RadioGroup
            defaultValue="link"
            onChange={(event) => {
              setSource(event.target.value);
            }}
          >
            <FormControlLabel value="link" control={<Radio />} label={translate('createUnit.link')} />
            <FormControlLabel value="file" control={<Radio />} label={translate('createUnit.file')} />
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {source === 'link' && (
            <FormTextFieldStandard
              variant="outlined"
              label={translate('createUnit.link')}
              value={linkValue}
              onChange={(event) => {
                setLinkValue(event);
                setError(null);
              }}
              onBlur={async () => {
                if (!linkValue) {
                  data.data = '';
                  setError(null);
                  return;
                }

                const validationError = await validateImageUrl(linkValue);

                if (validationError === ImageUrlError.InvalidFormat) {
                  setError('Неверный формат ссылки.');
                  return;
                }

                if (validationError === ImageUrlError.RemoteValidationFailed) {
                  setError('Ссылка небезопасна или не ведёт на изображение.');
                  return;
                }

                setError(null);
                data.data = linkValue;
              }}
              errorMessage={error}
            />
          )}

          {source === 'file' && (
            <FileUpload
              uploadDomain={uploadDomain}
              onChange={(event) => (data.data = event)}
              fileType={FileType.IMAGE}
            />
          )}
          <FormTextFieldStandard
            variant="outlined"
            label={translate('phrases.description')}
            multiline
            value={data.indexName}
            onChange={(event) => {
              data.indexName = event;
            }}
          />
        </Box>
      </Box>
    );
  },
);
